import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGuestContext } from '../context/GuestIdentityContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Bug, Lightbulb, MessageSquare, Send, Camera, X, Palette, Heart, Flame, Loader2, AlertTriangle, AlertCircle, Zap, Star, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// File size limits
const MAX_FILE_SIZE_MB = 2;
const MAX_FILES = 3;

// Priority/urgency options
const PRIORITY_OPTIONS = [
  { value: 'showstopper', label: 'Showstopper', icon: AlertTriangle, color: 'bg-red-100 border-red-300 text-red-700', description: 'Blocks critical functionality' },
  { value: 'urgent', label: 'Urgent', icon: AlertCircle, color: 'bg-orange-100 border-orange-300 text-orange-700', description: 'Important but workaround exists' },
  { value: 'normal', label: 'Normal', icon: Zap, color: 'bg-blue-100 border-blue-300 text-blue-700', description: 'Standard priority' },
  { value: 'nice-to-have', label: 'Nice to Have', icon: Star, color: 'bg-green-100 border-green-300 text-green-700', description: 'Low priority enhancement' }
];

/**
 * Floating feedback button for beta testers AND guests
 * Captures current URL automatically
 */
export function BetaFeedbackButton() {
  const { user } = useAuth();
  const { guestId } = useGuestContext();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Get referring page from query param (when navigated from another page)
  const searchParams = new URLSearchParams(location.search);
  const referringPage = searchParams.get('from');
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [screenshots, setScreenshots] = useState([]); // Array of {file, preview, uploadedUrl}
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    type: 'bug',
    priority: 'normal',
    title: '',
    description: '',
    url: '',
    club_id: '',
    guest_email: ''
  });

  // Show for logged-in users OR guests (everyone can give feedback)
  const isGuest = !user;

  // Auto-capture URL when dialog opens
  useEffect(() => {
    if (isOpen) {
      const pageUrl = referringPage
        ? `${window.location.origin}${referringPage}`
        : window.location.href;
      setForm(prev => ({
        ...prev,
        url: pageUrl
      }));
    }
  }, [isOpen, location, referringPage]);

  // Upload a file to the server (works for both users and guests)
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const headers = {};
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(`${API}/api/uploads/screenshot`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Upload failed');
    }

    return await res.json();
  };

  // Handle screenshot file selection
  const handleScreenshotSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (screenshots.length >= MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} screenshots allowed`);
      return;
    }

    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`Screenshot must be less than ${MAX_FILE_SIZE_MB}MB. Your file: ${fileSizeMB}MB`);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshots(prev => [...prev, { 
        file, 
        preview: reader.result, 
        uploadedUrl: null,
        uploading: false 
      }]);
    };
    reader.readAsDataURL(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle paste for screenshots
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            if (screenshots.length >= MAX_FILES) {
              toast.error(`Maximum ${MAX_FILES} screenshots allowed`);
              return;
            }
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
              toast.error(`Screenshot must be less than ${MAX_FILE_SIZE_MB}MB. Your file: ${fileSizeMB}MB`);
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
              setScreenshots(prev => [...prev, { 
                file, 
                preview: reader.result, 
                uploadedUrl: null,
                uploading: false 
              }]);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    }
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const clearScreenshots = () => {
    setScreenshots([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error('Please fill in the title');
      return;
    }

    setSubmitting(true);
    try {
      // Upload any screenshots that haven't been uploaded yet
      let uploadedUrls = [];
      if (screenshots.length > 0) {
        setUploading(true);
        for (const ss of screenshots) {
          if (ss.uploadedUrl) {
            uploadedUrls.push(ss.uploadedUrl);
          } else {
            try {
              const result = await uploadFile(ss.file);
              uploadedUrls.push(`${API}${result.url}`);
            } catch (err) {
              toast.error(`Failed to upload screenshot: ${err.message}`);
              setSubmitting(false);
              setUploading(false);
              return;
            }
          }
        }
        setUploading(false);
      }

      const payload = {
        type: form.type,
        priority: form.priority,
        title: form.title,
        description: form.description || '',
        url: form.url,
        club_id: form.club_id || null,
        screenshot_url: uploadedUrls.length > 0 ? uploadedUrls[0] : null // Primary screenshot
        // Note: Additional screenshots could be added to description or a separate field
      };

      // Add guest fields if not logged in
      if (isGuest) {
        payload.guest_email = form.guest_email;
        payload.guest_id = guestId;
      }

      // If multiple screenshots, append URLs to description
      if (uploadedUrls.length > 1) {
        payload.description += `\n\n**Additional Screenshots:**\n${uploadedUrls.slice(1).map((url, i) => `- Screenshot ${i + 2}: ${url}`).join('\n')}`;
      }

      const headers = {
        'Content-Type': 'application/json'
      };
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API}/api/beta-feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Feedback submitted! Thank you for helping improve the platform.');
        setIsOpen(false);
        setForm({
          type: 'bug',
          priority: 'normal',
          title: '',
          description: '',
          url: window.location.href,
          club_id: '',
          guest_email: ''
        });
        clearScreenshots();
      } else {
        // Handle error responses
        let errorMessage = 'Failed to submit feedback';
        try {
          const err = await res.json();
          errorMessage = err.detail || errorMessage;
        } catch {
          // Response wasn't JSON
          errorMessage = `Server error (${res.status})`;
        }
        
        // Provide user-friendly messages for common errors
        if (res.status === 401 && !isGuest) {
          errorMessage = 'Session expired. Please log in again.';
        } else if (res.status === 400 && isGuest) {
          errorMessage = 'Please provide your email address.';
        }
        
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Feedback submission error:', err);
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug': return <Bug className="h-4 w-4" />;
      case 'feature': return <Lightbulb className="h-4 w-4" />;
      case 'suggestion': return <MessageSquare className="h-4 w-4" />;
      case 'design': return <Palette className="h-4 w-4" />;
      case 'compliment': return <Heart className="h-4 w-4" />;
      case 'flame': return <Flame className="h-4 w-4" />;
      default: return <Bug className="h-4 w-4" />;
    }
  };

  // Show feedback button for everyone (users and guests)
  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-5 z-50 flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full shadow-lg transition-all hover:scale-105"
        data-testid="beta-feedback-button"
      >
        <Bug className="h-4 w-4" />
        <span className="text-sm font-medium">Feedback</span>
      </button>

      {/* Feedback Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-amber-500" />
              Submit Feedback
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Type Selection */}
            <div className="grid grid-cols-3 gap-2 mb-2">
              {['bug', 'feature', 'suggestion'].map((type) => (
                <button
                  key={type}
                  onClick={() => setForm({ ...form, type })}
                  className={`flex flex-col items-center justify-center gap-1 py-2 px-2 rounded-lg border transition-all ${
                    form.type === type
                      ? type === 'bug'
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : type === 'feature'
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-green-50 border-green-300 text-green-700'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {getTypeIcon(type)}
                  <span className="text-xs capitalize">{type}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {['design', 'compliment', 'flame'].map((type) => (
                <button
                  key={type}
                  onClick={() => setForm({ ...form, type })}
                  className={`flex flex-col items-center justify-center gap-1 py-2 px-2 rounded-lg border transition-all ${
                    form.type === type
                      ? type === 'design'
                        ? 'bg-purple-50 border-purple-300 text-purple-700'
                        : type === 'compliment'
                        ? 'bg-pink-50 border-pink-300 text-pink-700'
                        : 'bg-orange-50 border-orange-300 text-orange-700'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {getTypeIcon(type)}
                  <span className="text-xs capitalize">{type}</span>
                </button>
              ))}
            </div>

            {/* Guest Email (only shown for guests) */}
            {isGuest && (
              <div>
                <Label htmlFor="guest_email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-amber-500" />
                  Your Email *
                </Label>
                <Input
                  id="guest_email"
                  type="email"
                  placeholder="your@email.com (so we can follow up)"
                  value={form.guest_email}
                  onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                  data-testid="feedback-guest-email"
                  className="mt-1"
                />
                <p className="text-xs text-gray-400 mt-1">We'll only use this to respond to your feedback</p>
              </div>
            )}

            {/* Title */}
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder={form.type === 'bug' ? "What's broken?" : form.type === 'feature' ? "What would you like?" : "Your suggestion..."}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="feedback-title"
              />
            </div>

            {/* Priority Selection */}
            <div>
              <Label>Priority</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {PRIORITY_OPTIONS.map((opt) => {
                  const IconComp = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setForm({ ...form, priority: opt.value })}
                      className={`flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-lg border transition-all text-center ${
                        form.priority === opt.value ? opt.color : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                      title={opt.description}
                    >
                      <IconComp className="h-4 w-4" />
                      <span className="text-xs">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description <span className="text-gray-400 text-xs">(optional)</span></Label>
              <Textarea
                id="description"
                placeholder={form.type === 'bug' 
                  ? "Steps to reproduce, what happened vs what you expected..." 
                  : "Describe your idea in detail..."}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                onPaste={handlePaste}
                rows={3}
                data-testid="feedback-description"
              />
              <p className="text-xs text-gray-400 mt-1">Tip: You can paste a screenshot here (Ctrl/Cmd+V)</p>
            </div>

            {/* Screenshot */}
            <div>
              <Label>Screenshots <span className="text-gray-400 text-xs">(optional, max {MAX_FILES})</span></Label>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleScreenshotSelect}
                className="hidden"
              />
              
              {/* Screenshot previews */}
              {screenshots.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {screenshots.map((ss, index) => (
                    <div key={`screenshot-${index}-${ss.file?.name || 'uploaded'}`} className="relative w-20 h-20">
                      <img 
                        src={ss.preview} 
                        alt={`Screenshot ${index + 1}`} 
                        className="w-full h-full object-cover rounded-lg border bg-gray-50"
                      />
                      <button
                        onClick={() => removeScreenshot(index)}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add screenshot button */}
              {screenshots.length < MAX_FILES && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-colors flex items-center justify-center gap-2 text-gray-500 hover:text-amber-600"
                >
                  <Camera className="h-4 w-4" />
                  <span className="text-sm">
                    {screenshots.length === 0 ? 'Add Screenshot' : 'Add Another'}
                  </span>
                </button>
              )}
              
              <p className="text-xs text-gray-400 mt-1">
                Max {MAX_FILE_SIZE_MB}MB each. Tip: Paste with Ctrl/Cmd+V
              </p>
            </div>

            {/* URL (auto-captured) */}
            <div>
              <Label htmlFor="url">Page URL</Label>
              <Input
                id="url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="text-sm text-gray-600"
                data-testid="feedback-url"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-captured from current page</p>
            </div>

            {/* Club-specific toggle */}
            {user?.club_id && (
              <div>
                <Label>Scope</Label>
                <Select value={form.club_id || 'all'} onValueChange={(val) => setForm({ ...form, club_id: val === 'all' ? '' : val })}>
                  <SelectTrigger data-testid="feedback-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Platform-wide (visible to all beta users)</SelectItem>
                    <SelectItem value={user.club_id}>Club-specific (my club only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || uploading || (isGuest && !form.guest_email)} 
              data-testid="feedback-submit"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Feedback
                  <Send className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BetaFeedbackButton;
